import React, {useState} from 'react'
import {IsNotLoggedIn} from '../../auth/AccessConditionals'
import {useAuthContext} from '../../auth/AuthContextProvider'
import dashboardHeroImg from '../../images/mhclg/dashboard-hero.png'
import dashboardSearchImg from '../../images/mhclg/dashboard-search.png'
import dashboardWireframeImg from '../../images/mhclg/dashboard-wireframe.png'
import documentIconImg from '../../images/mhclg/document-icon.png'
import magnifyingGlassIconImg from '../../images/mhclg/magnifying-glass-icon.png'
import {ReactComponent as BottomDivider} from '../../images/shapes/curves/curve-1.svg'
import {ReactComponent as DividerCurve} from '../../images/shapes/curves/curve-2.svg'
import personIconImg from '../../images/mhclg/person-icon.png'
import './MHCLGLandingPage.css'

interface AuthContext {
  loginWithRedirect: (targetUrl?: string) => void
}

interface FAQItem {
  question: string
  answer: string
}

const faqItems: FAQItem[] = [
  {
    question: 'What is a data protection impact assessment?',
    answer:
      "A Data Protection Impact Assessment (DPIA) is a process designed to help teams identify and minimise the data protection risks of a project or initiative, especially when processing personal data. It's a key requirement under the UK GDPR and the Data Protection Act 2018, and is particularly important when introducing new technologies or data processing activities that could significantly impact individuals' privacy."
  },
  {
    question: 'Who needs to complete a data protection impact assessment?',
    answer:
      "A Data Protection Impact Assessment (DPIA) should be completed by the Data Controller, Project Leads, Business Owners or assigned project team personnel that is planning to carry out a data processing activity, especially one that is likely to result in a high risk to individuals' rights and freedoms."
  },
  {
    question: 'When should I complete a data protection impact assessment?',
    answer:
      "You should complete a Data Protection Impact Assessment (DPIA) before starting any data processing activity that is likely to pose a high risk to individuals' privacy. It's a proactive measure, not something to be done after the fact."
  },
  {
    question: 'Where can I access a data protection impact assessment?',
    answer:
      'To access a Data Protection Impact Assessment (DPIA), go to your Apps Sign-in Extension site and click on "My Apps". From your My Apps Dashboard, locate and select the Dapian application. Once the app opens, log in by clicking "Continue with MHCLG". This will take you into the Dapian platform, where you can create, view, or manage DPIAs as needed.'
  },
  {
    question: 'Can I save my data protection assessments and complete it at a later time?',
    answer:
      'Yes you are able to save an ongoing case and return at a later date. You can do this by selecting the save feature at the top of the task form. This will save the progress and leave the form in an open state ready to be worked on.'
  },
  {
    question: 'Who is responsible for reviewing completed data protection impact assessments?',
    answer:
      'The responsibility for reviewing completed Data Protection Impact Assessments (DPIAs) typically falls to the Data Protection Officer (DPO) or the Transparency Data Protection (TDP) within MHCLG.'
  },
  {
    question: 'Where can I find support relating to data protection impact assessments?',
    answer:
      'You can find support and guidance for completing Data Protection Impact Assessments (DPIAs) from the Transparency Data Protection (TDP), Little Fish, Live Service or Dapian product management team.'
  }
]

interface DPIACardProps {
  text: string
  imageSrc?: string
  icon?: React.ComponentType<any>
}

function DPIACard({text, imageSrc, icon: Icon}: DPIACardProps) {
  return (
    <div className="card border-0 shadow rounded h-100">
      <div className="card-body text-center p-4">
        <div className="mb-3">
          {imageSrc ? (
            <img src={imageSrc} alt="" className="dpia-card-image" />
          ) : Icon ? (
            <Icon height={40} className="text-primary" />
          ) : null}
        </div>
        <p className="text-muted mb-0">{text}</p>
      </div>
    </div>
  )
}

export const MHCLGLandingPage: React.FC = () => {
  const {loginWithRedirect} = useAuthContext() as AuthContext
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  const handleLogin = () => {
    loginWithRedirect()
  }

  const handleSixtySecondCheck = () => {
    loginWithRedirect('/assessment/dpia-communities')
  }

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index)
  }

  return (
    <div className="mhclg-landing-page">
      {/* Section 1: Hero */}
      <section className="hero-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h1 className="display-4 text-primary mb-2">Data Protection Impact Assessments</h1>
              <p className="lead text-muted mb-4">Plan with privacy, deliver with confidence.</p>

              <IsNotLoggedIn>
                <button className="btn btn-primary btn-lg mb-4" onClick={handleLogin}>
                  Log in
                </button>
              </IsNotLoggedIn>

              <p className="text-muted mb-3">
                Complete a new DPIA quickly and confidently. This page explains what a DPIA is, why it matters, where to
                find it, and how to complete it.
              </p>
              <p className="text-muted mb-4">
                Start with the 60-second check to see if you need to complete a DPIA; then use our tool to capture risks
                and mitigations, and record approvals.
              </p>

              <IsNotLoggedIn>
                <button className="btn btn-outline-primary btn-lg" onClick={handleSixtySecondCheck}>
                  Start 60-second check
                </button>
              </IsNotLoggedIn>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-container text-center">
                <img src={dashboardHeroImg} alt="DPIA Dashboard" className="img-fluid hero-dashboard-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Data Protection at MHCLG */}
      <section className="data-protection-section position-relative">
        <div className="section-wave-bg">
          <svg viewBox="0 0 1440 400" preserveAspectRatio="none">
            <path d="M0,0 Q720,0 1440,56 L1440,400 L0,400 Z" fill="#f5f5f5" />
          </svg>
        </div>
        <div className="section-content py-5">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 offset-lg-4 text-right">
                <h2 className="text-primary mb-4">Data Protection at MHCLG</h2>
                <div className="data-protection-points">
                  <div className="d-flex align-items-start justify-content-end mb-2">
                    <span className="mr-2 text-muted">-</span>
                    <p className="mb-0 text-muted text-right">DPIAs help us earn public trust, meet GDPR requirements, and make data protection easier.</p>
                  </div>
                  <div className="d-flex align-items-start justify-content-end mb-2">
                    <span className="mr-2 text-muted">-</span>
                    <p className="mb-0 text-muted text-right">Completion of DPIAs, before procurement, build or data collection, avoids costly fixes later.</p>
                  </div>
                  <div className="d-flex align-items-start justify-content-end">
                    <span className="mr-2 text-muted">-</span>
                    <p className="mb-0 text-muted text-right">Behind every dataset is a person, DPIAs are how we build services people feel safe to use.</p>
                  </div>
                </div>
                <p className="text-primary font-italic small mt-4 mb-0">
                  "You are not alone, contact the Data Protection Team for guidance and support."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is a DPIA? */}
      <section className="py-5">
        <div className="container">
          <h2 className="text-primary mb-5">What is a data protection impact assessment?</h2>
          <div className="row">
            <div className="col-md-4 mb-4">
              <DPIACard
                imageSrc={documentIconImg}
                text="A structured assessment of how your project will use personal data."
              />
            </div>
            <div className="col-md-4 mb-4">
              <DPIACard
                imageSrc={magnifyingGlassIconImg}
                text="Identifies privacy impacts and risks to individuals arising from personal data use."
              />
            </div>
            <div className="col-md-4 mb-4">
              <DPIACard
                imageSrc={personIconImg}
                text="Plans and records controls to reduce and mitigate risks and demonstrate accountability."
              />
            </div>
          </div>
        </div>
      </section>

      <div className="position-relative">
        <div className="shape shape-bottom shape-fluid-x svg-shim text-white">
          <DividerCurve />
        </div>
      </div>

      {/* Why is a DPIA important? */}
      <section className="why-important-section position-relative">
        <div className="section-wave-bg">
          <svg viewBox="0 0 1440 400" preserveAspectRatio="none">
            <path d="M0,56 Q720,0 1440,0 L1440,400 L0,400 Z" fill="#f5f5f5" />
          </svg>
        </div>
        <div className="section-content py-5">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-5 mb-4 mb-lg-0">
                <img src={dashboardWireframeImg} alt="DPIA Dashboard" className="img-fluid dashboard-illustration" />
              </div>
              <div className="col-lg-7 text-right">
                <h2 className="text-primary mb-1">Why is a DPIA important?</h2>
                <p className="text-muted font-italic mb-4">Build trust from day one.</p>
                <div className="why-important-points">
                  <p className="mb-2 text-muted"><strong>For people</strong> - A DPIA reduces the chance of personal harm, bias, intrusion or surprise.</p>
                  <p className="mb-2 text-muted"><strong>For the project</strong> - A DPIA avoids rework, unlocks approvals, and de-risks go-live decisions.</p>
                  <p className="mb-0 text-muted"><strong>For the department</strong> - A DPIA proves accountability and supports privacy and transparency.</p>
                </div>
                <p className="text-primary font-italic small mt-4 mb-0">
                  "Complete the DPIA early. It is faster to design privacy than to incorporate retrospectively."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who should complete a DPIA? */}
      <section className="py-5 bg-light">
        <div className="container">
          <h2 className="text-primary mb-5">
            Who should complete a data protection impact assessment?
          </h2>
          <div className="who-should-list">
            <div className="d-flex align-items-start mb-2">
              <span className="bullet-circle bg-primary mr-3"></span>
              <p className="mb-0 text-muted">People involved in a project or activity that involves sharing data externally.</p>
            </div>
            <div className="d-flex align-items-start mb-2">
              <span className="bullet-circle bg-primary mr-3"></span>
              <p className="mb-0 text-muted">People involved in a project or activity that involves collecting or using personal or sensitive data.</p>
            </div>
            <div className="d-flex align-items-start">
              <span className="bullet-circle bg-primary mr-3"></span>
              <p className="mb-0 text-muted">People involved in a project or activity that involves using new tools or systems that process personal data.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Where do I find an existing DPIA? */}
      <section className="where-find-section position-relative">
        <div className="section-wave-bg">
          <svg viewBox="0 0 1440 400" preserveAspectRatio="none">
            <path d="M0,56 Q720,0 1440,0 L1440,400 L0,400 Z" fill="#f5f5f5" />
          </svg>
        </div>
        <div className="section-content py-5">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-5 mb-4 mb-lg-0">
                <img src={dashboardSearchImg} alt="DPIA Dashboard" className="img-fluid dashboard-illustration" />
              </div>
              <div className="col-lg-7 text-right">
                <h2 className="text-primary mb-1">Where do I find an existing DPIA?</h2>
                <p className="text-muted font-italic mb-4">All DPIAs can be found in one place.</p>
                <div className="find-dpia-list">
                  <p className="mb-2 text-muted text-justify">- Use our data protection impact assessment tool to start a new DPIA or continue where you left off.</p>
                  <p className="mb-2 text-muted text-justify">- If you prefer a quick-start check, take the 60-second screening to confirm if a DPIA is required.</p>
                  <p className="mb-0 text-muted text-justify">- If you are continuing or reviewing an existing DPIA, please access through your DPIA dashboard.</p>
                </div>
                <p className="text-primary font-italic small mt-4 mb-0">
                  "From start to finish, we keep your DPIAs safe and secure, ready to review at any time."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="position-relative">
        <div className="shape shape-bottom shape-fluid-x svg-shim text-light">
          <BottomDivider />
        </div>
      </div>

      {/* Guidance and Support */}
      <section className="py-5 bg-light">
        <div className="container">
          <h2 className="text-primary mb-5">Guidance and Support</h2>
          <div className="row">
            <div className="col-lg-8">
              <p className="text-muted mb-4">
                The DPIA tool is aligned with UK General Data Protection regulation (GDPR) and the Data Protection
                Act 2018. It helps you find, assess and manage risks when accessing people's personal data.
              </p>
              <p className="text-primary mb-3">For further guidance, users are encouraged to:</p>
            </div>
          </div>
          <div className="guidance-list mb-4">
            <div className="d-flex align-items-start justify-content-between mb-2">
              <p className="mb-0 text-muted">Refer to internal DPIA policy documents and templates.</p>
              <span className="bullet-circle bg-primary ml-3"></span>
            </div>
            <div className="d-flex align-items-start justify-content-between mb-2">
              <p className="mb-0 text-muted">Use the DRM system for risk assessment support.</p>
              <span className="bullet-circle bg-primary ml-3"></span>
            </div>
            <div className="d-flex align-items-start justify-content-between">
              <p className="mb-0 text-muted">Contact the Transparency and Data Protection Team.</p>
              <span className="bullet-circle bg-primary ml-3"></span>
            </div>
          </div>
          <p className="text-primary font-italic mb-0">
            We are available to support you each step of the way in completing your data protection impact assessment.
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-5">
        <div className="container">
          <h2 className="text-primary mb-5">Frequently asked questions</h2>
          <div className="row">
            <div className="col-lg-10">
              <div className="faq-accordion">
                {faqItems.map((item, index) => (
                  <div className="faq-item card border-0 mb-2" key={index}>
                    <div
                      className={`faq-header card-header bg-white py-3 ${expandedFaq === index ? 'expanded' : ''}`}
                      onClick={() => toggleFaq(index)}
                      role="button"
                      aria-expanded={expandedFaq === index}
                      aria-controls={`faq-collapse-${index}`}
                    >
                      <div className="d-flex align-items-center">
                        <span className="faq-toggle text-primary mr-3">{expandedFaq === index ? '−' : '+'}</span>
                        <h6 className="mb-0 font-weight-normal text-dark">{item.question}</h6>
                      </div>
                    </div>
                    {expandedFaq === index && (
                      <div id={`faq-collapse-${index}`} className="card-body py-3 ml-4">
                        <p className="text-muted mb-0 small">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
